import { useEffect, useMemo } from 'react';
import useAtelier5Store from '../../../stores/useAtelier5Store';
import { buildAnalysisMeasures } from '../ActiviteA/buildMeasuresTable';

// Local copy of the risk-scenario index map (same logic as
// RiskScenarioListView.buildRiskScenarioIndexMap) — kept here to avoid
// importing the heavy RiskScenarioListView (Gantt) into the suivi page.
const buildIndexMap = (strategicScenarios) => {
  const map = {};
  let idx = 0;
  for (const ss of (strategicScenarios || [])) {
    const scenarioRef = ss.referenceCode || ss.id;
    for (const ap of (ss.attackPaths || [])) {
      const id = typeof ap === 'string' ? ap : ap.id;
      if (id && !map[id]) {
        map[id] = {
          shortRef: `R${++idx}`,
          originalRef: typeof ap === 'string' ? ap : (ap.referenceCode || ap.name || ap.id),
          name: typeof ap === 'string' ? '' : (ap.name || ''),
          scenarioRef,
        };
      }
    }
  }
  return map;
};

const toStatus = (m) => {
  if (m.status) return m.status;
  const impl = m.implementation || m.decision;
  if (impl === 'implemented' || impl === 'apply_before') return 'implemented';
  if (impl === 'in_progress') return 'in_progress';
  return 'planned';
};

/**
 * useSuiviMeasures — single source of truth for the fiche de suivi.
 * Returns the exact same rows as the Atelier 5 measures table
 * (SecurityMeasuresTable via buildAnalysisMeasures), normalized for the
 * suivi filters (name / responsiblePerson / status present).
 * Backend checklist data (questions, approvals) is untouched — only the
 * `measures` list is replaced.
 */
const useSuiviMeasures = (analysisId) => {
  const synthesisData = useAtelier5Store((s) => s.synthesisData);
  const isDataLoaded = useAtelier5Store((s) => s.isDataLoaded);
  const storedAnalysisId = useAtelier5Store((s) => s.analysisId);
  const isLoading = useAtelier5Store((s) => s.isLoading);
  const error = useAtelier5Store((s) => s.error);
  const loadData = useAtelier5Store((s) => s.loadData);

  useEffect(() => {
    if (!analysisId) return;
    if (!isDataLoaded || storedAnalysisId !== analysisId) {
      loadData(analysisId);
    }
  }, [analysisId, isDataLoaded, storedAnalysisId, loadData]);

  const strategicScenarios = useMemo(
    () => synthesisData?.strategicScenarios || [],
    [synthesisData],
  );

  const allMeasures = useMemo(() => {
    if (!strategicScenarios.length) return [];
    const indexMap = buildIndexMap(strategicScenarios);
    return buildAnalysisMeasures(strategicScenarios, indexMap).map((m) => ({
      ...m,
      name: m.name || m.title || m.measureName || m.description || 'Sans nom',
      responsiblePerson: m.responsiblePerson || m.responsibleParty || '',
      status: toStatus(m),
    }));
  }, [strategicScenarios]);

  return {
    strategicScenarios,
    allMeasures,
    // Ready = this analysis's Atelier 5 data is in the store.
    isReady: isDataLoaded && (!analysisId || storedAnalysisId === analysisId),
    isLoading,
    error,
  };
};

export default useSuiviMeasures;
